/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BIRTH TIME RECTIFICATION (BTR)
 * END-TO-END SEARCH ORCHESTRATOR & LIFECYCLE ENGINE (PHASE 5)
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0, Section 10 & 11
 * Document ID: VJ-BTR-V65-SPEC-001
 * Architecture: GOV-061, GOV-062, GOV-063 [PROPOSED]
 *
 * Core Principles:
 * 1. End-to-End Orchestration: Unifies Phase 1 (Two-Axis Uncertainty Contracts),
 *    Phase 2 (Dynamic Historical IANA Timezone Resolution), Phase 3 (Continuous
 *    Astrological Plateau Bisection), and Phase 4 (Categorical Evidence & Variable
 *    Clustering Engine).
 * 2. Unambiguous 8-Outcome Taxonomy:
 *    - SUCCESS_CONVERGENT_INTERVAL
 *    - SUCCESS_MULTIPLE_CANDIDATE_CLUSTERS
 *    - INCONCLUSIVE_INSUFFICIENT_EVIDENCE
 *    - INCONCLUSIVE_CONTRADICTORY_EVIDENCE
 *    - INCONCLUSIVE_DISPARATE_PEAKS
 *    - ACTION_REQUIRED_PARTIAL_COVERAGE
 *    - ACTION_REQUIRED_UNRESOLVED_TIMEZONE
 *    - ACTION_REQUIRED_INVALID_INPUT
 * 3. Orthogonal 4-State Lifecycle & Provenance:
 *    Preserves recorded civil time as immutable baseline; outputs analytical
 *    plateau as RECTIFIED_INTERVAL; strictly bars automatic adoption of working
 *    or confirmed times.
 * ============================================================================
 */

import {
  AstrologicalPlateauInterval,
  BtrLifeEventInput,
  BtrProvenanceRecord,
  BtrSearchOptions,
  BtrSearchResult,
  FourBtrStates,
  PlateauEvidenceEvaluation,
  SearchCoverageAudit,
  SearchOutcomeStatus,
  TemporalRegion,
  TwoAxisBirthUncertainty
} from '../types/btr';
import {
  computeSearchCoverageStatus,
  validateSearchCoverageAudit,
  validateTwoAxisUncertainty,
  validateBtrLifeEvents
} from './btrContractValidation';
import { formatUtcToLocal, getUtcOffsetSecondsForInstant, resolveLocalToUtc } from './btrTimezoneResolver';
import { scanPlateausInWindow } from './btrPlateauEngine';
import {
  CandidateChartContext,
  evaluatePlateauAgainstLifeEvents
} from './btrEvidenceEngine';

// ============================================================================
// HELPER: CALENDAR DATE RANGE ITERATION
// ============================================================================

/**
 * Safely computes next calendar date YYYY-MM-DD.
 */
export function getNextCalendarDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  const ny = dt.getUTCFullYear();
  const nm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const nd = String(dt.getUTCDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

/**
 * Returns array of calendar date strings from startDateStr to endDateStr inclusive.
 */
export function getCalendarDateRange(startDateStr: string, endDateStr: string): string[] {
  const dates: string[] = [];
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  const [ey, em, ed] = endDateStr.split('-').map(Number);
  let cur = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));

  while (cur.getTime() <= end.getTime()) {
    const y = cur.getUTCFullYear();
    const m = String(cur.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cur.getUTCDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return dates;
}


/**
 * Anchors a plateau's civil date, local clock times and UTC offset to its own resolved UTC instants, so the
 * candidate Kundali is cast at exactly the scanned instant. Window start dates and window-level offsets are
 * wrong for plateaus after local midnight or across an offset transition inside the window.
 */
export function anchorPlateauToResolvedInstant(
  plateau: AstrologicalPlateauInterval,
  timezoneId: string,
  fallbackLocalDate: string,
  fallbackOffsetMinutes: number
): AstrologicalPlateauInterval {
  if (!plateau.startUtcInstant) {
    plateau.localDate = plateau.localDate || fallbackLocalDate;
    if (plateau.timezoneOffsetMinutes === undefined) plateau.timezoneOffsetMinutes = fallbackOffsetMinutes;
    return plateau;
  }
  const startMs = Date.parse(plateau.startUtcInstant);
  const startLocal = formatUtcToLocal(startMs, timezoneId);
  plateau.localDate = startLocal.localDate;
  plateau.startLocalTime = startLocal.localTime;
  plateau.timezoneOffsetMinutes = getUtcOffsetSecondsForInstant(startMs, timezoneId) / 60;
  if (plateau.endUtcInstant) {
    const endMs = Date.parse(plateau.endUtcInstant);
    plateau.endLocalTime = formatUtcToLocal(endMs, timezoneId).localTime;
    plateau.intervalString = `${plateau.startLocalTime} - ${plateau.endLocalTime}`;
    // Display-only reference, never selected (no midpoint auto-adoption)
    const midMs = startMs + Math.floor((endMs - startMs) / 2000) * 1000;
    plateau.mathematicalMidpointReference = formatUtcToLocal(midMs, timezoneId).localTime;
  }
  return plateau;
}

// ============================================================================
// DEFAULT CONSTANTS & PROVISIONAL BENCHMARK TARGETS
// ============================================================================

export const DEFAULT_SEARCH_OPTIONS: Required<BtrSearchOptions> = {
  maxEvaluationsAllowed: 2500, // Provisional benchmark target
  maxTimeMsAllowed: 400,       // Target latency budget (400ms)
  stepSeconds: 120,            // Adaptive coarse sampling step (2 min)
  denseVerify: false,          // 1-second dense uniformity verification
  ayanamsha: 'Lahiri',
  nodeConvention: 'mean',
  allowMultiDayCascade: false,
  allowBroadSearch: false
};

// ============================================================================
// HELPER: BUILD EMPTY SEARCH COVERAGE AUDIT
// ============================================================================

function buildEmptySearchCoverageAudit(
  uncertainty: Partial<TwoAxisBirthUncertainty> | null | undefined,
  status: SearchOutcomeStatus,
  reason: string
): SearchCoverageAudit {
  const u = uncertainty || {};
  const dateStart = u.axisBDate?.earliestLocalDate || '1900-01-01';
  const dateEnd = u.axisBDate?.latestLocalDate || '1900-01-01';
  const timeStart = u.axisATime?.earliestLocalTime || '00:00:00';
  const timeEnd = u.axisATime?.latestLocalTime || '00:00:00';

  return {
    requestedEnvelope: {
      axisBDateRange: { start: dateStart, end: dateEnd },
      axisATimeRange: { start: timeStart, end: timeEnd },
      totalTheoreticalSeconds: 0
    },
    budgetLimits: {
      maxEvaluationsAllowed: 2500,
      actualEvaluationsExecuted: 0,
      maxTimeMsAllowed: 400,
      actualTimeMsTaken: 0,
      budgetExceeded: false
    },
    stagesExecuted: [],
    evaluatedRegions: [],
    prunedRegions: [],
    unevaluatedRegions: [
      {
        startUtc: '1900-01-01T00:00:00Z',
        endUtc: '1900-01-01T00:00:00Z',
        regionType: 'UNEVALUATED',
        reason
      }
    ],
    coverageStatus: 'INSUFFICIENT_DATA',
    completenessRatio: 0.0,
    auditTimestamp: new Date().toISOString(),
    engineVersion: 'VJ-BTR-V65-ENGINE-v3.0'
  };
}

// ============================================================================
// PRIMARY END-TO-END ORCHESTRATION PIPELINE
// ============================================================================

/**
 * Executes full end-to-end Birth Time Rectification search across all 5 phases.
 */
export function executeBtrSearch(
  uncertainty: TwoAxisBirthUncertainty,
  lifeEvents: BtrLifeEventInput[] = [],
  options?: BtrSearchOptions
): BtrSearchResult {
  const mergedOptions: Required<BtrSearchOptions> = {
    ...DEFAULT_SEARCH_OPTIONS,
    ...options
  };

  const executionTimestamp = new Date().toISOString();
  const reportedTime = uncertainty?.axisATime?.reportedLocalTime || '12:00:00';

  // --------------------------------------------------------------------------
  // STEP 1: Contract Validation (Fail-Closed)
  // --------------------------------------------------------------------------
  const validationRes = validateTwoAxisUncertainty(uncertainty);
  if (!validationRes.isValid) {
    const errorMsg = `Contract Validation Failed: ${validationRes.errors.join('; ')}`;
    const emptyAudit = buildEmptySearchCoverageAudit(
      uncertainty,
      'ACTION_REQUIRED_INVALID_INPUT',
      errorMsg
    );
    const initialProvenance: BtrProvenanceRecord = {
      state: 'RECORDED',
      timestamp: executionTimestamp,
      reason: `Validation failure: ${errorMsg}`,
      snapshot: { recordedTime: reportedTime }
    };
    const emptyState: FourBtrStates = {
      recordedTime: reportedTime,
      rectifiedInterval: null,
      workingTime: null,
      confirmedTime: null,
      lifecycleState: 'RECORDED',
      isProvisional: true,
      provenanceLedger: [initialProvenance]
    };

    return {
      outcomeStatus: 'ACTION_REQUIRED_INVALID_INPUT',
      convergentPlateaus: [],
      disqualifiedPlateaus: [],
      allEvaluatedPlateaus: [],
      coverageAudit: emptyAudit,
      provenanceRecord: initialProvenance,
      stateSnapshot: emptyState,
      timestamp: executionTimestamp,
      message: errorMsg
    };
  }

  const lifeEventsValidation = validateBtrLifeEvents(lifeEvents);
  if (!lifeEventsValidation.isValid) {
    const errorMsg = `Life Events Validation Failed: ${lifeEventsValidation.errors.join('; ')}`;
    const emptyAudit = buildEmptySearchCoverageAudit(
      uncertainty,
      'ACTION_REQUIRED_INVALID_INPUT',
      errorMsg
    );
    const initialProvenance: BtrProvenanceRecord = {
      state: 'RECORDED',
      timestamp: executionTimestamp,
      reason: `Validation failure: ${errorMsg}`,
      snapshot: { recordedTime: reportedTime }
    };
    const emptyState: FourBtrStates = {
      recordedTime: reportedTime,
      rectifiedInterval: null,
      workingTime: null,
      confirmedTime: null,
      lifecycleState: 'RECORDED',
      isProvisional: true,
      provenanceLedger: [initialProvenance]
    };

    return {
      outcomeStatus: 'ACTION_REQUIRED_INVALID_INPUT',
      errorCode: lifeEventsValidation.errorCode,
      convergentPlateaus: [],
      disqualifiedPlateaus: [],
      allEvaluatedPlateaus: [],
      coverageAudit: emptyAudit,
      provenanceRecord: initialProvenance,
      stateSnapshot: emptyState,
      timestamp: executionTimestamp,
      message: errorMsg
    };
  }

  const validatedLifeEvents = lifeEventsValidation.validatedData || lifeEvents;

  // --------------------------------------------------------------------------
  // STEP 2: Discontinuous vs Continuous Envelope Verification
  // --------------------------------------------------------------------------
  const earliestDate = uncertainty.axisBDate.earliestLocalDate;
  const latestDate = uncertainty.axisBDate.latestLocalDate;
  const reportedDate = uncertainty.axisBDate.reportedLocalDate;
  const earliestTime = uncertainty.axisATime.earliestLocalTime;
  const latestTime = uncertainty.axisATime.latestLocalTime;
  const tzId = uncertainty.location.timezoneId;

  // Determine continuous vs discontinuous envelope:
  // Case A: Single calendar date (earliestDate === latestDate)
  // Case B: Midnight-crossing single continuous interval: latestDate is immediate next calendar day
  //         AND earliestLocalTime > latestLocalTime (e.g. 23:00 to 01:00 across midnight)
  let isContinuousEnvelope = false;
  let scanStartDate = reportedDate;
  let scanEndDate = reportedDate;

  if (earliestDate === latestDate) {
    isContinuousEnvelope = true;
    scanStartDate = earliestDate;
    scanEndDate = latestDate;
  } else {
    // Check if it represents an immediate next day midnight crossing
    const dStartMs = Date.parse(`${earliestDate}T00:00:00Z`);
    const dEndMs = Date.parse(`${latestDate}T00:00:00Z`);
    const diffDays = Math.round((dEndMs - dStartMs) / (24 * 3600 * 1000));

    if (diffDays === 1 && earliestTime > latestTime) {
      isContinuousEnvelope = true;
      scanStartDate = earliestDate;
      scanEndDate = latestDate;
    }
  }

  // Check if discontinuous multi-day search is authorized (AGENTS.md §7.2 Opt-In Broad Searches)
  if (!isContinuousEnvelope) {
    const isMultiDayAuthorized = Boolean(
      options?.allowMultiDayCascade ||
      options?.allowBroadSearch
    );

    if (!isMultiDayAuthorized) {
      // Discontinuous multi-day search requested without explicit opt-in:
      // Cannot silently prune unsearched dates or claim complete coverage (AGENTS.md §7.2)
      const discontinuousMsg = `Discontinuous multi-day search window requested [dates: ${earliestDate} to ${latestDate}, daily hours: ${earliestTime} to ${latestTime}]. The canonical continuous plateau engine evaluates single continuous temporal intervals. Multi-day discontinuous search grids cannot claim complete coverage without multi-day coarse cascade execution (Stages 1–3) or composite daily sub-audits. Partial evaluation of reportedLocalDate is halted to prevent silent pruning and inaccurate complete coverage claims.`;
      const emptyAudit = buildEmptySearchCoverageAudit(
        uncertainty,
        'ACTION_REQUIRED_PARTIAL_COVERAGE',
        discontinuousMsg
      );
      emptyAudit.coverageStatus = 'TRUNCATED_BY_BUDGET';
      const initialProvenance: BtrProvenanceRecord = {
        state: 'RECORDED',
        timestamp: executionTimestamp,
        reason: `Discontinuous search gap: ${discontinuousMsg}`,
        snapshot: { recordedTime: reportedTime }
      };
      const emptyState: FourBtrStates = {
        recordedTime: reportedTime,
        rectifiedInterval: null,
        workingTime: null,
        confirmedTime: null,
        lifecycleState: 'RECORDED',
        isProvisional: true,
        provenanceLedger: [initialProvenance]
      };

      return {
        outcomeStatus: 'ACTION_REQUIRED_PARTIAL_COVERAGE',
        convergentPlateaus: [],
        disqualifiedPlateaus: [],
        allEvaluatedPlateaus: [],
        coverageAudit: emptyAudit,
        provenanceRecord: initialProvenance,
        stateSnapshot: emptyState,
        timestamp: executionTimestamp,
        message: discontinuousMsg
      };
    }
  }

  // --------------------------------------------------------------------------
  // STEP 3: Plateau Scanning & Search Coverage Accounting
  // --------------------------------------------------------------------------
  const plateausToEvaluate: AstrologicalPlateauInterval[] = [];
  let coverageAudit: SearchCoverageAudit;

  if (isContinuousEnvelope) {
    // Single continuous temporal interval
    const startRes = resolveLocalToUtc({
      localDate: scanStartDate,
      localClockTime: earliestTime,
      timezoneId: tzId,
      latitude: uncertainty.location.latitude,
      longitude: uncertainty.location.longitude,
      placeName: uncertainty.location.placeName
    });

    const endRes = resolveLocalToUtc({
      localDate: scanEndDate,
      localClockTime: latestTime,
      timezoneId: tzId,
      latitude: uncertainty.location.latitude,
      longitude: uncertainty.location.longitude,
      placeName: uncertainty.location.placeName
    });

    if (startRes.status !== 'RESOLVED_EXACT' || endRes.status !== 'RESOLVED_EXACT') {
      const tzError = `Timezone Resolution Failed for window [${earliestTime}, ${latestTime}] in ${tzId}: start=${startRes.status}, end=${endRes.status}`;
      const emptyAudit = buildEmptySearchCoverageAudit(
        uncertainty,
        'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
        tzError
      );
      const initialProvenance: BtrProvenanceRecord = {
        state: 'RECORDED',
        timestamp: executionTimestamp,
        reason: `Timezone gap/ambiguity: ${tzError}`,
        snapshot: { recordedTime: reportedTime }
      };
      const emptyState: FourBtrStates = {
        recordedTime: reportedTime,
        rectifiedInterval: null,
        workingTime: null,
        confirmedTime: null,
        lifecycleState: 'RECORDED',
        isProvisional: true,
        provenanceLedger: [initialProvenance]
      };

      return {
        outcomeStatus: 'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
        convergentPlateaus: [],
        disqualifiedPlateaus: [],
        allEvaluatedPlateaus: [],
        coverageAudit: emptyAudit,
        provenanceRecord: initialProvenance,
        stateSnapshot: emptyState,
        timestamp: executionTimestamp,
        message: tzError
      };
    }

    const startUtc = startRes.resolvedCandidate!.utcInstant;
    const endUtc = endRes.resolvedCandidate!.utcInstant;

    const scanResult = scanPlateausInWindow({
      startUtc,
      endUtc,
      latitude: uncertainty.location.latitude,
      longitude: uncertainty.location.longitude,
      timezoneId: tzId,
      placeName: uncertainty.location.placeName,
      maxEvaluations: mergedOptions.maxEvaluationsAllowed,
      maxDurationMs: mergedOptions.maxTimeMsAllowed,
      customStepSeconds: mergedOptions.stepSeconds,
      denseVerify: mergedOptions.denseVerify
    });

    for (const p of scanResult.plateaus) {
      plateausToEvaluate.push(anchorPlateauToResolvedInstant(p, tzId, scanStartDate, uncertainty.location.timezoneOffsetMinutes ?? 330));
    }

    coverageAudit = scanResult.coverageAudit;
  } else {
    // Multi-Day Coarse-to-Fine Search Cascade
    const dateList = getCalendarDateRange(earliestDate, latestDate);
    const isDailyMidnightCrossing = earliestTime > latestTime;

    // Resolve overall envelope bounds
    const firstDay = dateList[0];
    const lastDay = dateList[dateList.length - 1];
    const lastDayEnd = isDailyMidnightCrossing ? getNextCalendarDate(lastDay) : lastDay;

    const firstStartRes = resolveLocalToUtc({
      localDate: firstDay,
      localClockTime: earliestTime,
      timezoneId: tzId,
      latitude: uncertainty.location.latitude,
      longitude: uncertainty.location.longitude,
      placeName: uncertainty.location.placeName
    });

    const lastEndRes = resolveLocalToUtc({
      localDate: lastDayEnd,
      localClockTime: latestTime,
      timezoneId: tzId,
      latitude: uncertainty.location.latitude,
      longitude: uncertainty.location.longitude,
      placeName: uncertainty.location.placeName
    });

    if (firstStartRes.status !== 'RESOLVED_EXACT' || lastEndRes.status !== 'RESOLVED_EXACT') {
      const tzError = `Timezone Resolution Failed for boundary windows in ${tzId}: start=${firstStartRes.status}, end=${lastEndRes.status}`;
      const emptyAudit = buildEmptySearchCoverageAudit(
        uncertainty,
        'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
        tzError
      );
      const initialProvenance: BtrProvenanceRecord = {
        state: 'RECORDED',
        timestamp: executionTimestamp,
        reason: `Timezone gap/ambiguity: ${tzError}`,
        snapshot: { recordedTime: reportedTime }
      };
      const emptyState: FourBtrStates = {
        recordedTime: reportedTime,
        rectifiedInterval: null,
        workingTime: null,
        confirmedTime: null,
        lifecycleState: 'RECORDED',
        isProvisional: true,
        provenanceLedger: [initialProvenance]
      };

      return {
        outcomeStatus: 'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
        convergentPlateaus: [],
        disqualifiedPlateaus: [],
        allEvaluatedPlateaus: [],
        coverageAudit: emptyAudit,
        provenanceRecord: initialProvenance,
        stateSnapshot: emptyState,
        timestamp: executionTimestamp,
        message: tzError
      };
    }

    const envStartUtc = firstStartRes.resolvedCandidate!.utcInstant;
    const envEndUtc = lastEndRes.resolvedCandidate!.utcInstant;
    const envStartSec = Math.floor(new Date(envStartUtc).getTime() / 1000);
    const envEndSec = Math.floor(new Date(envEndUtc).getTime() / 1000);
    const totalTheoreticalSeconds = Math.max(0, envEndSec - envStartSec);

    let accumulatedEvaluations = 0;
    const cascadeStartPerf = performance.now();
    const allEvaluatedRegions: TemporalRegion[] = [];
    const allPrunedRegions: TemporalRegion[] = [];
    const allUnevaluatedRegions: TemporalRegion[] = [];
    let prevWindowEndUtc: string | null = null;
    let budgetExceeded = false;

    for (let i = 0; i < dateList.length; i++) {
      const curDate = dateList[i];
      const curEndDate = isDailyMidnightCrossing ? getNextCalendarDate(curDate) : curDate;

      const wStartRes = resolveLocalToUtc({
        localDate: curDate,
        localClockTime: earliestTime,
        timezoneId: tzId,
        latitude: uncertainty.location.latitude,
        longitude: uncertainty.location.longitude,
        placeName: uncertainty.location.placeName
      });

      const wEndRes = resolveLocalToUtc({
        localDate: curEndDate,
        localClockTime: latestTime,
        timezoneId: tzId,
        latitude: uncertainty.location.latitude,
        longitude: uncertainty.location.longitude,
        placeName: uncertainty.location.placeName
      });

      if (wStartRes.status !== 'RESOLVED_EXACT' || wEndRes.status !== 'RESOLVED_EXACT') {
        const tzError = `Timezone Resolution Failed on date ${curDate} in ${tzId}: start=${wStartRes.status}, end=${wEndRes.status}`;
        const emptyAudit = buildEmptySearchCoverageAudit(
          uncertainty,
          'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
          tzError
        );
        const initialProvenance: BtrProvenanceRecord = {
          state: 'RECORDED',
          timestamp: executionTimestamp,
          reason: `Timezone gap/ambiguity: ${tzError}`,
          snapshot: { recordedTime: reportedTime }
        };
        const emptyState: FourBtrStates = {
          recordedTime: reportedTime,
          rectifiedInterval: null,
          workingTime: null,
          confirmedTime: null,
          lifecycleState: 'RECORDED',
          isProvisional: true,
          provenanceLedger: [initialProvenance]
        };

        return {
          outcomeStatus: 'ACTION_REQUIRED_UNRESOLVED_TIMEZONE',
          convergentPlateaus: [],
          disqualifiedPlateaus: [],
          allEvaluatedPlateaus: [],
          coverageAudit: emptyAudit,
          provenanceRecord: initialProvenance,
          stateSnapshot: emptyState,
          timestamp: executionTimestamp,
          message: tzError
        };
      }

      const wStartUtc = wStartRes.resolvedCandidate!.utcInstant;
      const wEndUtc = wEndRes.resolvedCandidate!.utcInstant;

      // Inter-day temporal region accounting
      if (i === 0) {
        if (new Date(wStartUtc).getTime() > envStartSec * 1000) {
          allPrunedRegions.push({
            startUtc: envStartUtc,
            endUtc: wStartUtc,
            regionType: 'PRUNED',
            reason: 'DAILY_TIME_WINDOW_PRUNING: Inter-day interval outside user-authorized daily clock-time uncertainty window'
          });
        }
      } else if (prevWindowEndUtc) {
        if (new Date(wStartUtc).getTime() > new Date(prevWindowEndUtc).getTime()) {
          allPrunedRegions.push({
            startUtc: prevWindowEndUtc,
            endUtc: wStartUtc,
            regionType: 'PRUNED',
            reason: 'DAILY_TIME_WINDOW_PRUNING: Inter-day interval outside user-authorized daily clock-time uncertainty window'
          });
        }
      }

      // Check remaining computational budget
      const remEvaluations = mergedOptions.maxEvaluationsAllowed - accumulatedEvaluations;
      const remTimeMs = mergedOptions.maxTimeMsAllowed - (performance.now() - cascadeStartPerf);

      if (remEvaluations <= 0 || remTimeMs <= 0) {
        budgetExceeded = true;
        allUnevaluatedRegions.push({
          startUtc: wStartUtc,
          endUtc: envEndUtc,
          regionType: 'UNEVALUATED',
          reason: remTimeMs <= 0 ? 'EXECUTION_TIMEOUT_EXCEEDED' : 'EVALUATION_BUDGET_EXHAUSTED'
        });
        prevWindowEndUtc = envEndUtc;
        break;
      }

      // Scan plateaus in current daily window
      const dayScan = scanPlateausInWindow({
        startUtc: wStartUtc,
        endUtc: wEndUtc,
        latitude: uncertainty.location.latitude,
        longitude: uncertainty.location.longitude,
        timezoneId: tzId,
        placeName: uncertainty.location.placeName,
        maxEvaluations: remEvaluations,
        maxDurationMs: Math.max(1, Math.round(remTimeMs)),
        customStepSeconds: mergedOptions.stepSeconds,
        denseVerify: mergedOptions.denseVerify
      });

      accumulatedEvaluations += dayScan.evaluationCount;
      allEvaluatedRegions.push(...dayScan.coverageAudit.evaluatedRegions);
      allUnevaluatedRegions.push(...dayScan.coverageAudit.unevaluatedRegions);

      for (const p of dayScan.plateaus) {
        plateausToEvaluate.push(anchorPlateauToResolvedInstant(p, tzId, curDate, uncertainty.location.timezoneOffsetMinutes ?? 330));
      }

      prevWindowEndUtc = wEndUtc;

      // Handle daily budget exhaustion
      if (dayScan.coverageAudit.budgetLimits.budgetExceeded || dayScan.coverageAudit.unevaluatedRegions.length > 0) {
        budgetExceeded = true;
        if (i < dateList.length - 1) {
          allUnevaluatedRegions.push({
            startUtc: wEndUtc,
            endUtc: envEndUtc,
            regionType: 'UNEVALUATED',
            reason: 'EVALUATION_BUDGET_EXHAUSTED'
          });
        }
        prevWindowEndUtc = envEndUtc;
        break;
      }
    }

    // Anchor trailing pruned region if needed
    if (!budgetExceeded && prevWindowEndUtc && new Date(prevWindowEndUtc).getTime() < envEndSec * 1000) {
      allPrunedRegions.push({
        startUtc: prevWindowEndUtc,
        endUtc: envEndUtc,
        regionType: 'PRUNED',
        reason: 'DAILY_TIME_WINDOW_PRUNING: Inter-day interval outside user-authorized daily clock-time uncertainty window'
      });
    }

    const coverageStatus = computeSearchCoverageStatus({
      evaluatedRegions: allEvaluatedRegions,
      prunedRegions: allPrunedRegions,
      unevaluatedRegions: allUnevaluatedRegions,
      budgetExceeded,
      totalTheoreticalSeconds
    });

    let evaluatedSecSum = 0;
    for (const r of allEvaluatedRegions) {
      evaluatedSecSum += Math.round((new Date(r.endUtc).getTime() - new Date(r.startUtc).getTime()) / 1000);
    }
    const completenessRatio = totalTheoreticalSeconds > 0
      ? Math.min(1.0, evaluatedSecSum / totalTheoreticalSeconds)
      : 0;

    coverageAudit = {
      requestedEnvelope: {
        axisBDateRange: { start: envStartUtc.split('T')[0], end: envEndUtc.split('T')[0] },
        axisATimeRange: { start: envStartUtc.split('T')[1].substring(0, 8), end: envEndUtc.split('T')[1].substring(0, 8) },
        totalTheoreticalSeconds
      },
      budgetLimits: {
        maxEvaluationsAllowed: mergedOptions.maxEvaluationsAllowed,
        actualEvaluationsExecuted: accumulatedEvaluations,
        maxTimeMsAllowed: mergedOptions.maxTimeMsAllowed,
        actualTimeMsTaken: Math.round(performance.now() - cascadeStartPerf),
        budgetExceeded
      },
      stagesExecuted: [
        {
          stageName: 'MicroPlateau',
          stepMinutes: (mergedOptions.stepSeconds || 60) / 60,
          evaluatedCount: allEvaluatedRegions.length,
          prunedCount: allPrunedRegions.length,
          pruningCriteria: 'DAILY_TIME_WINDOW_PRUNING'
        }
      ],
      evaluatedRegions: allEvaluatedRegions,
      prunedRegions: allPrunedRegions,
      unevaluatedRegions: allUnevaluatedRegions,
      coverageStatus,
      completenessRatio,
      auditTimestamp: executionTimestamp,
      engineVersion: 'VJ-BTR-V65-ENGINE-v3.0'
    };

    const auditValidation = validateSearchCoverageAudit(coverageAudit);
    if (!auditValidation.isValid) {
      throw new Error(`Multi-day SearchCoverageAudit integrity validation failed: ${auditValidation.errors.join(', ')}`);
    }
  }

  // --------------------------------------------------------------------------
  // STEP 4: Categorical Evidence Evaluation per Candidate Plateau
  // --------------------------------------------------------------------------
  const candidateContext: CandidateChartContext = {
    birthDate: reportedDate,
    birthTime: reportedTime,
    latitude: uncertainty.location.latitude,
    longitude: uncertainty.location.longitude,
    placeName: uncertainty.location.placeName,
    timezoneOffsetMinutes: uncertainty.location.timezoneOffsetMinutes ?? 330,
    ayanamsha: mergedOptions.ayanamsha,
    nodeConvention: mergedOptions.nodeConvention
  };

  const allEvaluatedPlateaus: PlateauEvidenceEvaluation[] = [];
  const convergentPlateaus: PlateauEvidenceEvaluation[] = [];
  const disqualifiedPlateaus: PlateauEvidenceEvaluation[] = [];

  for (const plateau of plateausToEvaluate) {
    const evalRes = evaluatePlateauAgainstLifeEvents(plateau, validatedLifeEvents, candidateContext);
    allEvaluatedPlateaus.push(evalRes);

    if (evalRes.isDisqualified) {
      disqualifiedPlateaus.push(evalRes);
    } else if (evalRes.convergenceVerdict === 'CONVERGENT') {
      convergentPlateaus.push(evalRes);
    }
  }

  // --------------------------------------------------------------------------
  // STEP 5: Outcome Classification (Spec §10 Taxonomy)
  // --------------------------------------------------------------------------
  let outcomeStatus: SearchOutcomeStatus;
  let message: string;

  if (coverageAudit.coverageStatus === 'TRUNCATED_BY_BUDGET' || coverageAudit.budgetLimits.budgetExceeded) {
    outcomeStatus = 'ACTION_REQUIRED_PARTIAL_COVERAGE';
    message = 'Search execution halted due to computational evaluation or execution time budget limits; partial coverage disclosed via SearchCoverageAudit.';
  } else if (validatedLifeEvents.length === 0) {
    outcomeStatus = 'INCONCLUSIVE_INSUFFICIENT_EVIDENCE';
    message = 'No life events were supplied for categorical corroboration; candidates cannot be resolved beyond broad plateau window.';
  } else if (allEvaluatedPlateaus.length === 0) {
    outcomeStatus = 'INCONCLUSIVE_INSUFFICIENT_EVIDENCE';
    message = 'Zero continuous plateaus discovered within evaluated search window.';
  } else if (convergentPlateaus.length === 0 && disqualifiedPlateaus.length === allEvaluatedPlateaus.length) {
    outcomeStatus = 'INCONCLUSIVE_CONTRADICTORY_EVIDENCE';
    message = `All ${allEvaluatedPlateaus.length} candidate plateau(s) were disqualified by critical Shastric contradictions on Tier A events.`;
  } else if (convergentPlateaus.length === 1) {
    outcomeStatus = 'SUCCESS_CONVERGENT_INTERVAL';
    const p = convergentPlateaus[0].plateauInterval;
    const dateLabel = p.localDate ? `${p.localDate} ` : '';
    message = `Single continuous plateau strongly supported across independent clusters with zero critical contradictions (${dateLabel}${p.intervalString}).`;
  } else if (convergentPlateaus.length > 1) {
    outcomeStatus = 'SUCCESS_MULTIPLE_CANDIDATE_CLUSTERS';
    const clusterDates = Array.from(new Set(convergentPlateaus.map(c => c.plateauInterval.localDate).filter(Boolean)));
    const dateContext = clusterDates.length > 1 ? ` across ${clusterDates.length} distinct dates (${clusterDates.join(', ')})` : '';
    message = `Discovered ${convergentPlateaus.length} candidate plateaus supported across independent clusters${dateContext}; additional discriminating life events required.`;
  } else {
    // Check if partial plateaus have equal disparate peaks
    const partialCount = allEvaluatedPlateaus.filter(p => !p.isDisqualified && p.convergenceVerdict === 'PARTIAL').length;
    if (partialCount > 1) {
      outcomeStatus = 'INCONCLUSIVE_DISPARATE_PEAKS';
      message = `Candidate plateaus exhibit disparate partial support (${partialCount} partial candidates); inconclusive without definitive multi-cluster convergence.`;
    } else {
      outcomeStatus = 'INCONCLUSIVE_INSUFFICIENT_EVIDENCE';
      message = 'Supplied life events provide insufficient independent cluster support to confirm candidate interval.';
    }
  }

  // --------------------------------------------------------------------------
  // STEP 6: Four BTR States & Lifecycle Provenance
  // Invariant: Engine NEVER auto-adopts working or confirmed times.
  // --------------------------------------------------------------------------
  const activeRectifiedInterval = convergentPlateaus.length === 1
    ? convergentPlateaus[0].plateauInterval
    : null;

  const activeLifecycleState = convergentPlateaus.length === 1
    ? 'RECTIFIED_INTERVAL'
    : 'RECORDED';

  const provenanceRecord: BtrProvenanceRecord = {
    state: activeLifecycleState,
    timestamp: executionTimestamp,
    reason: `BTR V6.5 Search Engine: ${outcomeStatus} (${message})`,
    snapshot: {
      recordedTime: reportedTime,
      workingTime: null,
      confirmedTime: null,
      rectifiedInterval: activeRectifiedInterval ? activeRectifiedInterval.intervalString : null
    }
  };

  const stateSnapshot: FourBtrStates = {
    recordedTime: reportedTime,
    rectifiedInterval: activeRectifiedInterval,
    workingTime: null,
    confirmedTime: null,
    lifecycleState: activeLifecycleState,
    isProvisional: true, // Always provisional until deliberate human confirmation
    provenanceLedger: [provenanceRecord]
  };

  return {
    outcomeStatus,
    convergentPlateaus,
    disqualifiedPlateaus,
    allEvaluatedPlateaus,
    coverageAudit,
    provenanceRecord,
    stateSnapshot,
    timestamp: executionTimestamp,
    message
  };
}


/**
 * Async wrapper for API and asynchronous job execution.
 */
export async function executeBtrSearchAsync(
  uncertainty: TwoAxisBirthUncertainty,
  lifeEvents: BtrLifeEventInput[] = [],
  options?: BtrSearchOptions
): Promise<BtrSearchResult> {
  return Promise.resolve(executeBtrSearch(uncertainty, lifeEvents, options));
}

